/**
 * The frame the game is played inside.
 *
 * M5 put the table inside a document: a tall branded header, a strip of mode
 * and seed controls, a permanent 310px column of panels, and the table in
 * whatever was left. It measured well and it read as an admin screen with a
 * green rectangle in it, and on a laptop or a phone the player's own hand fell
 * below the fold while the game was asking them to discard.
 *
 * So the shell is now a fixed viewport rather than a page. Two rows: one thin
 * bar, and everything else. Nothing scrolls during normal play, because a
 * table you have to scroll to is a picture of a table.
 *
 *   .gameshell   100dvh, overflow hidden
 *   .gamebar     one strip: the way out, the three modes, language, menu
 *   .gamestage   the rest of the screen - the table, edge to edge
 *   .sheet       secondary surfaces, over the table, only when asked for
 *
 * Everything that used to live in the permanent column - coach, log, help,
 * seed, sound, the opening animation preference, the multiplayer room - goes
 * into the sheet. None of it is lost, none of it is one click further away
 * than it deserves to be, and none of it takes a quarter of the table any
 * more.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { LanguageSelector, useI18n } from "../i18n";
import { BrandMark } from "../portal/BrandMark";
import { ModeSelector } from "./ModeSelector";
import type { AppMode } from "./ModeSelector";

export interface GameShellProps {
  mode: AppMode;
  onMode: (mode: AppMode) => void;
  onExit: () => void;
  /**
   * The stage. `table` hands the whole area to the felt and never scrolls;
   * `page` is for the screens that are not a table yet - the multiplayer
   * lobby, the lesson overview - and scrolls as a centred column.
   */
  variant?: "table" | "page";
  /** Secondary surfaces, rendered inside the menu sheet. */
  menu?: ReactNode;
  /** One compact item allowed in the bar, e.g. the Online connection pill. */
  barStatus?: ReactNode;
  /** Transient alert, floated over the stage rather than pushing it down. */
  notice?: ReactNode;
  /** Full-stage overlay: the end-of-round panel. */
  overlay?: ReactNode;
  children: ReactNode;
}

export function GameShell({
  mode,
  onMode,
  onExit,
  variant = "table",
  menu,
  barStatus,
  notice,
  overlay,
  children,
}: GameShellProps) {
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const sheet = useRef<HTMLDivElement>(null);

  const close = useCallback((): void => {
    setMenuOpen(false);
    // Focus goes back where it came from, or the keyboard is left stranded in
    // a panel that is no longer on screen.
    trigger.current?.focus();
  }, []);

  // Escape closes the sheet, the way every other dialog on the web does.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
      }
    };
    globalThis.document?.addEventListener("keydown", onKey);
    return () => globalThis.document?.removeEventListener("keydown", onKey);
  }, [menuOpen, close]);

  // Opening moves focus into the sheet, so the first Tab lands inside it.
  useEffect(() => {
    if (!menuOpen) return;
    sheet.current?.querySelector<HTMLElement>("[data-sheet-close]")?.focus();
  }, [menuOpen]);

  return (
    <div className="gameshell" data-game-mode={mode} data-variant={variant}>
      {/*
        * One strip, and deliberately a thin one. It carries navigation only:
        * the way back to kikik.id, which of the three modes you are in, the
        * language, and the door to everything else. No game state lives here -
        * whose turn it is, how much wall is left and what was just discarded
        * are all readable on the table itself, which is where a player is
        * already looking.
        */}
      {/* The screen still needs a name. It is not drawn, because a branded
          title bar over a Mahjong table is the single thing that did most to
          make M5 read as an application, but a document with no h1 is a
          document a screen-reader user cannot orient in. */}
      <h1 className="visually-hidden">{t("app.title")}</h1>

      <header className="gamebar">
        <button type="button" className="gamebar__exit" onClick={onExit} aria-label={t("portal.exit.aria")}>
          <span className="gamebar__back" aria-hidden="true">
            ←
          </span>
          <BrandMark className="chip--sm" />
        </button>

        <ModeSelector mode={mode} onChange={onMode} compact />

        <div className="gamebar__right">
          {barStatus}
          <LanguageSelector />
          <button
            type="button"
            ref={trigger}
            className={`gamebar__menu ${menuOpen ? "gamebar__menu--open" : ""}`}
            aria-expanded={menuOpen}
            aria-controls={menuId}
            aria-label={t("shell.menu.open")}
            onClick={() => (menuOpen ? close() : setMenuOpen(true))}
          >
            <span className="gamebar__menu-glyph" aria-hidden="true" />
            <span className="gamebar__menu-word">{t("shell.menu")}</span>
          </button>
        </div>
      </header>

      <main className="gamestage" data-testid="game-stage" aria-label={t("shell.stage")}>
        {notice}
        {children}
      </main>

      {/*
        * The sheet is mounted only while it is open. A panel parked off-screen
        * is still in the tab order and still read out, and this one holds three
        * or four panels' worth of controls - so it goes away properly.
        */}
      {menuOpen && menu ? (
        <>
          <button
            type="button"
            className="sheet__scrim"
            aria-label={t("shell.menu.close")}
            tabIndex={-1}
            onClick={close}
          />
          <div
            className="sheet"
            id={menuId}
            ref={sheet}
            role="dialog"
            aria-modal="true"
            aria-label={t("shell.menu.title")}
            data-testid="game-menu"
          >
            <header className="sheet__bar">
              <h2 className="sheet__title">{t("shell.menu.title")}</h2>
              <button
                type="button"
                className="sheet__close"
                data-sheet-close=""
                aria-label={t("shell.menu.close")}
                onClick={close}
              >
                <span aria-hidden="true">✕</span>
              </button>
            </header>
            <div className="sheet__body">
              {menu}
              {/* The colophon that used to be the page footer. It is honest
                  product information - which tile set, one engine for all
                  three modes - and it belongs with the other reading matter
                  rather than under the table. */}
              <p className="sheet__colophon">
                <span>{t("app.footer.set")}</span>
                <span>{t("app.footer.engine")}</span>
              </p>
            </div>
          </div>
        </>
      ) : null}

      {overlay}
    </div>
  );
}

/** What the app shell hands a mode so the game bar can navigate. */
export interface ShellNav {
  onMode: (mode: AppMode) => void;
  onExit: () => void;
}
